import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
    calculateAgeFromDOB,
    calculateSizeTag,
    formatDateForInput,
    getMaxDateOfBirth,
    getMinDateOfBirth,
} from '../utils/dogUtils';
import SignaturePad from './SignaturePad';
import BreedSelect from './BreedSelect';
import '../styles/components/dog-form.css';
import { CARTA_CLAUSES, CARTA_INTRO, cartaAsText } from '../constants/cartaResponsiva';


const API_URL = process.env.REACT_APP_API_BASE_URL || '';

// A stored SI / NO answer as the form holds it. null/undefined stays '' — the
// question was never answered, which the letter shows as a blank, not as NO.
const boolToChoice = v => (v === true ? 'SI' : v === false ? 'NO' : '');

// The letter's own intake questions, submitted as one group.
const INTAKE_FIELDS = ['sex', 'sterilized', 'specialNotes'];

const DogForm = ({ onSubmit, onCancel, loading, editingDog = null }) => {
    const { authorizedFetch, user } = useAuth();
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        name: '',
        breed: '',
        age: '',
        ageMonths: '',
        dateOfBirth: '',
        weight: '',
        particularSigns: '',
        primaryOwnerId: '',
        secondaryOwnerId: '',
        // Carta Responsiva intake fields. The SI / NO ones are held as the
        // letter words them, with '' for "sin responder" — which the backend
        // keeps distinct from an explicit NO.
        sex: '',
        sterilized: '',
        specialNotes: '',
    });

    const [ageUnit, setAgeUnit] = useState('years');
    const [errors, setErrors] = useState({});
    const [owners, setOwners] = useState([]);
    const [ownersLoading, setOwnersLoading] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [imageLoading, setImageLoading] = useState(false);

    // Carta responsiva
    const [ownerSignature, setOwnerSignature] = useState('');
    // A stored signature is a base64 PNG that dwarfs the rest of the form. It is
    // loaded for display when editing, but resending it unchanged bloated every
    // save past the request size limit (413) and reset signedAt to today on each
    // unrelated edit. Only a signature touched in this session is submitted;
    // leaving it out makes the backend's $set skip responsiveLetter entirely.
    const [signatureDirty, setSignatureDirty] = useState(false);
    const [letterAccepted, setLetterAccepted] = useState(false);

    // New owner modal
    const [showNewOwnerModal, setShowNewOwnerModal] = useState(false);
    const [newOwnerForm, setNewOwnerForm] = useState({ fullName: '', phone: '', email: '', address: '' });
    const [newOwnerErrors, setNewOwnerErrors] = useState({});
    const [newOwnerSaving, setNewOwnerSaving] = useState(false);

    // Load owners
    useEffect(() => {
        if (!authorizedFetch) return;
        const load = async () => {
            setOwnersLoading(true);
            try {
                const res = await authorizedFetch(`${API_URL}/api/owners`);
                if (res.ok) setOwners(await res.json());
                else if (res.status === 401)
                    setErrors(p => ({ ...p, general: 'Fallo de autenticación. Por favor inicia sesión de nuevo.' }));
                else
                    setErrors(p => ({ ...p, general: `Error al cargar propietarios (${res.status}).` }));
            } catch {
                setErrors(p => ({ ...p, general: 'Error al cargar propietarios. Verifica tu conexión.' }));
            } finally {
                setOwnersLoading(false);
            }
        };
        load();
    }, [authorizedFetch]);

    // Pre-fill when editing
    useEffect(() => {
        if (!editingDog) return;
        const ownerIds = (editingDog.owners || []).map(o => o._id);
        const dogAge = editingDog.age || 0;
        const inMonths = dogAge < 2;
        setFormData({
            name: editingDog.name || '',
            breed: editingDog.breed || '',
            age: inMonths ? '' : (editingDog.age || ''),
            ageMonths: inMonths ? Math.round(dogAge * 12) : '',
            dateOfBirth: editingDog.dateOfBirth ? formatDateForInput(editingDog.dateOfBirth) : '',
            weight: editingDog.weight || '',
            particularSigns: editingDog.particularSigns || '',
            primaryOwnerId: ownerIds[0] || '',
            secondaryOwnerId: ownerIds[1] || '',
            sex: editingDog.sex || '',
            sterilized: boolToChoice(editingDog.sterilized),
            specialNotes: editingDog.specialNotes || '',
        });
        setAgeUnit(inMonths ? 'months' : 'years');
        if (editingDog.pictureUrl) setImagePreview(editingDog.pictureUrl);
        else if (editingDog.picture) setImagePreview(`${API_URL}/uploads/dogs/${editingDog.picture}`);
        else setImagePreview(null);
        setSelectedImage(null);
        if (editingDog.responsiveLetter?.ownerSignature) {
            setOwnerSignature(editingDog.responsiveLetter.ownerSignature);
            setLetterAccepted(true);
        }
    }, [editingDog]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(p => ({ ...p, [name]: value }));
        if (errors[name]) setErrors(p => ({ ...p, [name]: '' }));
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const valid = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!valid.includes(file.type)) {
            setErrors(p => ({ ...p, picture: 'Selecciona una imagen JPEG, PNG, GIF o WebP.' }));
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setErrors(p => ({ ...p, picture: 'La imagen debe ser menor a 10 MB.' }));
            return;
        }
        setSelectedImage(file);
        const reader = new FileReader();
        reader.onload = ev => setImagePreview(ev.target.result);
        reader.readAsDataURL(file);
        if (errors.picture) setErrors(p => ({ ...p, picture: '' }));
    };

    const handleImageRemove = async () => {
        if (editingDog && editingDog.picture && !selectedImage) {
            setImageLoading(true);
            try {
                const res = await authorizedFetch(`${API_URL}/api/dogs/${editingDog._id}/picture`, { method: 'DELETE' });
                if (res.ok) {
                    setImagePreview(null);
                    editingDog.picture = null;
                } else {
                    const err = await res.json();
                    setErrors(p => ({ ...p, picture: err.error || 'Error al quitar la foto.' }));
                }
            } catch {
                setErrors(p => ({ ...p, picture: 'Error al quitar la foto.' }));
            } finally {
                setImageLoading(false);
            }
        } else {
            setSelectedImage(null);
            setImagePreview(
                editingDog?.pictureUrl ||
                (editingDog?.picture ? `${API_URL}/uploads/dogs/${editingDog.picture}` : null)
            );
        }
    };

    const validate = () => {
        const e = {};
        if (owners.length === 0 && !ownersLoading)
            e.general = 'No hay propietarios disponibles. Agrega uno primero.';
        if (!formData.name.trim()) e.name = 'El nombre del perro es requerido.';
        if (!formData.breed.trim()) e.breed = 'La raza es requerida.';
        if (!formData.dateOfBirth) {
            const v = ageUnit === 'months' ? formData.ageMonths : formData.age;
            if (!v || v <= 0) e.age = 'Ingresa la edad o la fecha de nacimiento.';
            else if (ageUnit === 'years' && v > 30) e.age = 'Ingresa una edad válida (0–30 años).';
            else if (ageUnit === 'months' && v > 360) e.age = 'Ingresa una edad válida (0–360 meses).';
        }
        if (formData.dateOfBirth) {
            const dob = new Date(formData.dateOfBirth);
            const min = new Date(); min.setFullYear(min.getFullYear() - 30);
            if (dob > new Date()) e.dateOfBirth = 'La fecha de nacimiento no puede ser futura.';
            else if (dob < min) e.dateOfBirth = 'La fecha de nacimiento no puede ser hace más de 30 años.';
        }
        if (formData.weight && (formData.weight < 0 || formData.weight > 150))
            e.weight = 'Ingresa un peso válido (0–150 kg).';
        if (formData.particularSigns.length > 500)
            e.particularSigns = 'Máximo 500 caracteres.';
        if (!formData.primaryOwnerId) e.primaryOwnerId = 'El propietario principal es requerido.';
        if (formData.primaryOwnerId && formData.secondaryOwnerId &&
            formData.primaryOwnerId === formData.secondaryOwnerId)
            e.secondaryOwnerId = 'Debe ser diferente al propietario principal.';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const openNewOwnerModal = () => {
        setNewOwnerForm({ fullName: '', phone: '', email: '', address: '' });
        setNewOwnerErrors({});
        setShowNewOwnerModal(true);
    };

    const handleCreateOwner = async () => {
        const errs = {};
        if (!newOwnerForm.fullName.trim()) errs.fullName = 'El nombre es requerido.';
        if (!newOwnerForm.phone.trim()) errs.phone = 'El teléfono es requerido.';
        setNewOwnerErrors(errs);
        if (Object.keys(errs).length > 0) return;

        setNewOwnerSaving(true);
        try {
            const res = await authorizedFetch(`${API_URL}/api/owners`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: newOwnerForm.fullName.trim(),
                    phone: newOwnerForm.phone.trim(),
                    email: newOwnerForm.email.trim() || undefined,
                    address: newOwnerForm.address.trim() || undefined,
                }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                setNewOwnerErrors({ general: body.error || 'Error al crear propietario.' });
                return;
            }
            const created = await res.json();
            setOwners(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
            setFormData(p => ({ ...p, primaryOwnerId: created._id }));
            if (errors.primaryOwnerId) setErrors(p => ({ ...p, primaryOwnerId: '' }));
            setShowNewOwnerModal(false);
        } catch {
            setNewOwnerErrors({ general: 'Error de conexión.' });
        } finally {
            setNewOwnerSaving(false);
        }
    };

    const handleShareLetter = () => {
        const owner = owners.find(o => o._id === formData.primaryOwnerId);
        const ownerName = owner?.name || '';
        const phone = owner?.phone?.replace(/\D/g, '');
        const dogName = formData.name || '___________';

        const header = `*Happy Tails — Carta Responsiva*\n\nEstimado/a ${ownerName || '___________'},\nTe compartimos los términos y condiciones de uso de los servicios de Happy Tails para la mascota *${dogName}*. Por favor léelos con atención.\n\n`;
        const body = cartaAsText();
        const text = header + body;

        if (phone) {
            window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, '_blank');
        } else {
            navigator.clipboard?.writeText(text).then(() => alert('Carta copiada al portapapeles'));
        }
    };

    const buildOwnersList = () => {
        const list = [formData.primaryOwnerId].filter(Boolean);
        if (formData.secondaryOwnerId) list.push(formData.secondaryOwnerId);
        return list;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;

        const ownersList = buildOwnersList();

        if (selectedImage) {
            const fd = new FormData();
            fd.append('name', formData.name);
            fd.append('breed', formData.breed);
            if (formData.dateOfBirth) {
                fd.append('dateOfBirth', formData.dateOfBirth);
            } else {
                const yr = ageUnit === 'months'
                    ? parseFloat(formData.ageMonths) / 12
                    : parseFloat(formData.age);
                if (yr) fd.append('age', yr);
            }
            if (formData.weight) fd.append('weight', parseFloat(formData.weight));
            if (formData.particularSigns) fd.append('particularSigns', formData.particularSigns);
            // Always sent, empty included: '' tells the backend the answer was
            // cleared, while leaving the key out would mean "don't touch it".
            for (const field of INTAKE_FIELDS) fd.append(field, formData[field]);
            fd.append('owners', JSON.stringify(ownersList));
            fd.append('picture', selectedImage);
            // Sent whenever it was touched, empty string included: '' is how the
            // backend is told to erase a stored signature. Untouched, the field
            // stays out of the body entirely and the stored one is preserved.
            if (signatureDirty) fd.append('ownerSignature', ownerSignature);
            onSubmit(fd);
        } else {
            const data = { name: formData.name, breed: formData.breed, owners: ownersList };
            if (formData.dateOfBirth) {
                data.dateOfBirth = formData.dateOfBirth;
            } else {
                const yr = ageUnit === 'months'
                    ? parseFloat(formData.ageMonths) / 12
                    : parseFloat(formData.age);
                if (yr) data.age = yr;
            }
            if (formData.weight) data.weight = parseFloat(formData.weight);
            if (formData.particularSigns) data.particularSigns = formData.particularSigns;
            for (const field of INTAKE_FIELDS) data[field] = formData[field];
            if (signatureDirty) data.ownerSignature = ownerSignature;
            onSubmit(data);
        }
    };

    const handleCancel = () => {
        setFormData({ name:'', breed:'', age:'', ageMonths:'', dateOfBirth:'', weight:'', particularSigns:'', primaryOwnerId:'', secondaryOwnerId:'', sex:'', sterilized:'', specialNotes:'' });
        setAgeUnit('years');
        setErrors({});
        setSelectedImage(null);
        setImagePreview(null);
        setOwnerSignature('');
        setSignatureDirty(false);
        setLetterAccepted(false);
        onCancel();
    };

    const ageInfo  = formData.dateOfBirth ? calculateAgeFromDOB(formData.dateOfBirth) : null;
    const sizeTag  = formData.weight ? calculateSizeTag(parseFloat(formData.weight)) : null;
    const noOwners = owners.length === 0 && !ownersLoading;

    return (
        <form className="df-wrap" onSubmit={handleSubmit} noValidate>

            {/* ── Photo ── */}
            <div className="df-photo-section">
                <button
                    type="button"
                    className="df-photo-circle"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={imageLoading}
                    aria-label="Upload dog photo"
                >
                    {imagePreview
                        ? <img src={imagePreview} alt="Dog" className="df-photo-img" />
                        : <span className="df-photo-placeholder">📷</span>
                    }
                    <div className="df-photo-badge">+</div>
                </button>
                {imagePreview && (
                    <button
                        type="button"
                        className="df-photo-remove"
                        onClick={handleImageRemove}
                        disabled={imageLoading}
                    >
                        {imageLoading ? 'Quitando…' : 'Quitar foto'}
                    </button>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageSelect}
                />
                {errors.picture && <span className="df-field-error df-field-error--center">{errors.picture}</span>}
            </div>

            {/* ── General error ── */}
            {errors.general && (
                <div className="df-error-banner">{errors.general}</div>
            )}

            {/* ── Basic info ── */}
            <p className="df-section-label">Info básica</p>
            <div className="df-section">
                <div className="df-field">
                    <label className="df-label">Nombre <span className="df-required">*</span></label>
                    <input
                        className={`df-input ${errors.name ? 'df-input--err' : ''}`}
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="ej. Max"
                        autoComplete="off"
                    />
                    {errors.name && <span className="df-field-error">{errors.name}</span>}
                </div>
                <div className="df-divider" />
                <div className="df-field">
                    <label className="df-label" htmlFor="breed">Raza <span className="df-required">*</span></label>
                    <BreedSelect
                        inputId="breed"
                        value={formData.breed}
                        onChange={breed => {
                            setFormData(p => ({ ...p, breed }));
                            if (errors.breed) setErrors(p => ({ ...p, breed: '' }));
                        }}
                        error={!!errors.breed}
                    />
                    {errors.breed && <span className="df-field-error">{errors.breed}</span>}
                </div>
            </div>

            {/* ── Details ── */}
            <p className="df-section-label">Detalles</p>
            <div className="df-section">
                {/* Date of birth */}
                <div className="df-field">
                    <label className="df-label">Fecha de nacimiento</label>
                    <input
                        className={`df-input ${errors.dateOfBirth ? 'df-input--err' : ''}`}
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        min={getMinDateOfBirth()}
                        max={getMaxDateOfBirth()}
                    />
                    {ageInfo && (
                        <span className="df-hint-inline">
                            📅 {ageInfo.displayText}
                        </span>
                    )}
                    {errors.dateOfBirth && <span className="df-field-error">{errors.dateOfBirth}</span>}
                </div>

                <div className="df-divider" />

                {/* Age */}
                <div className="df-field df-field--row">
                    <div className="df-field-label-col">
                        <label className="df-label">Edad</label>
                        <span className="df-sub">sin fecha nac.</span>
                    </div>
                    <div className="df-age-row">
                        {formData.dateOfBirth ? (
                            <input
                                className="df-input df-input--sm"
                                type="number"
                                value={ageInfo?.years ?? 0}
                                disabled
                            />
                        ) : (
                            <input
                                className={`df-input df-input--sm ${errors.age ? 'df-input--err' : ''}`}
                                type="number"
                                name={ageUnit === 'months' ? 'ageMonths' : 'age'}
                                value={ageUnit === 'months' ? formData.ageMonths : formData.age}
                                onChange={handleChange}
                                min="0"
                                max={ageUnit === 'months' ? '360' : '30'}
                                step={ageUnit === 'months' ? '1' : '0.1'}
                                placeholder="0"
                            />
                        )}
                        <select
                            className="df-select df-select--sm"
                            value={ageUnit}
                            disabled={!!formData.dateOfBirth}
                            onChange={e => {
                                setAgeUnit(e.target.value);
                                setFormData(p => ({ ...p, age: '', ageMonths: '' }));
                            }}
                        >
                            <option value="years">años</option>
                            <option value="months">meses</option>
                        </select>
                    </div>
                </div>
                {errors.age && <span className="df-field-error df-field-error--indent">{errors.age}</span>}

                <div className="df-divider" />

                {/* Weight */}
                <div className="df-field df-field--row">
                    <div className="df-field-label-col">
                        <label className="df-label">Peso</label>
                        {sizeTag && (
                            <span className="df-size-badge" title={sizeTag.description}>
                                {sizeTag.tag}
                            </span>
                        )}
                    </div>
                    <div className="df-weight-row">
                        <input
                            className={`df-input df-input--sm ${errors.weight ? 'df-input--err' : ''}`}
                            type="number"
                            name="weight"
                            value={formData.weight}
                            onChange={handleChange}
                            min="0"
                            max="150"
                            step="0.1"
                            placeholder="0.0"
                        />
                        <span className="df-unit">kg</span>
                    </div>
                </div>
                {errors.weight && <span className="df-field-error df-field-error--indent">{errors.weight}</span>}
            </div>

            {/* ── Owner ── */}
            <p className="df-section-label">Propietario</p>
            <div className="df-section">
                <div className="df-field">
                    <div className="df-owner-label-row">
                        <label className="df-label">Propietario principal <span className="df-required">*</span></label>
                        <button type="button" className="df-new-owner-btn" onClick={openNewOwnerModal}>
                            + Nuevo propietario
                        </button>
                    </div>
                    {ownersLoading ? (
                        <span className="df-hint-inline">Cargando propietarios…</span>
                    ) : (
                        <select
                            className={`df-select df-select--full ${errors.primaryOwnerId ? 'df-input--err' : ''}`}
                            name="primaryOwnerId"
                            value={formData.primaryOwnerId}
                            onChange={handleChange}
                        >
                            <option value="">Seleccionar propietario…</option>
                            {owners.map(o => (
                                <option key={o._id} value={o._id}>
                                    {o.name}{o.phone ? ` · ${o.phone}` : ''}
                                </option>
                            ))}
                        </select>
                    )}
                    {errors.primaryOwnerId && <span className="df-field-error">{errors.primaryOwnerId}</span>}
                </div>

                <div className="df-divider" />

                <div className="df-field">
                    <label className="df-label">Propietario secundario <span className="df-sub">(opcional)</span></label>
                    <select
                        className={`df-select df-select--full ${errors.secondaryOwnerId ? 'df-input--err' : ''}`}
                        name="secondaryOwnerId"
                        value={formData.secondaryOwnerId}
                        onChange={handleChange}
                        disabled={ownersLoading}
                    >
                        <option value="">Ninguno</option>
                        {owners
                            .filter(o => o._id !== formData.primaryOwnerId)
                            .map(o => (
                                <option key={o._id} value={o._id}>
                                    {o.name}{o.phone ? ` · ${o.phone}` : ''}
                                </option>
                            ))}
                    </select>
                    {errors.secondaryOwnerId && <span className="df-field-error">{errors.secondaryOwnerId}</span>}
                </div>
            </div>

            {/* ── Notes ── */}
            <p className="df-section-label">Notas</p>
            <div className="df-section">
                <div className="df-field">
                    <label className="df-label">Señas particulares</label>
                    <textarea
                        className={`df-textarea ${errors.particularSigns ? 'df-input--err' : ''}`}
                        name="particularSigns"
                        value={formData.particularSigns}
                        onChange={handleChange}
                        placeholder="Marcas distintivas, cicatrices, alergias…"
                        rows={3}
                        maxLength={500}
                    />
                    <span className="df-char-count">{formData.particularSigns.length}/500</span>
                    {errors.particularSigns && <span className="df-field-error">{errors.particularSigns}</span>}
                </div>

                <div className="df-divider" />

                {/* "Algo que debamos saber acerca de su mascota" on the letter */}
                <div className="df-field">
                    <label className="df-label">Algo que debamos saber</label>
                    <textarea
                        className="df-textarea"
                        name="specialNotes"
                        value={formData.specialNotes}
                        onChange={handleChange}
                        placeholder="Cuidados especiales, alergias, miedos, medicina, sociabilidad, agresión, problemas físicos…"
                        rows={3}
                        maxLength={1000}
                    />
                    <span className="df-char-count">{formData.specialNotes.length}/1000</span>
                </div>
            </div>

            {/* ── Carta Responsiva intake questions ── */}
            <p className="df-section-label">Datos de la Carta</p>
            <div className="df-section">
                <div className="df-field df-field--row">
                    <div className="df-field-label-col">
                        <label className="df-label">Sexo</label>
                        <span className="df-sub">H o M</span>
                    </div>
                    <select className="df-select" name="sex" value={formData.sex} onChange={handleChange}>
                        <option value="">Sin responder</option>
                        <option value="H">H · Hembra</option>
                        <option value="M">M · Macho</option>
                    </select>
                </div>

                <div className="df-divider" />

                <div className="df-field df-field--row">
                    <div className="df-field-label-col">
                        <label className="df-label">Esterilizad@</label>
                    </div>
                    <select className="df-select" name="sterilized" value={formData.sterilized} onChange={handleChange}>
                        <option value="">Sin responder</option>
                        <option value="SI">SI</option>
                        <option value="NO">NO</option>
                    </select>
                </div>
            </div>

            {/* ── Carta Responsiva ── */}
            <p className="df-section-label">Carta Responsiva</p>
            <div className="df-section df-carta-section">

                {/* Letter body */}
                <div className="df-carta-scroll">
                    <div className="df-carta-header">
                        <div className="df-carta-logo-line">🐾 Happy Tails</div>
                        <h3 className="df-carta-title">Carta Responsiva</h3>
                        <p className="df-carta-intro">{CARTA_INTRO}</p>
                    </div>
                    <ol className="df-carta-list">
                        {CARTA_CLAUSES.map((clause, i) => (
                            <li key={i} className="df-carta-item">
                                {clause.text}
                                {clause.bullets && (
                                    <ul className="df-carta-sublist">
                                        {clause.bullets.map((bullet, j) => (
                                            <li key={j}>{bullet}</li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ol>
                </div>

                {/* Acceptance + Signature */}
                <div className="df-carta-bottom">
                    <label className="df-carta-accept-row">
                        <input
                            type="checkbox"
                            className="df-carta-checkbox"
                            checked={letterAccepted}
                            onChange={e => setLetterAccepted(e.target.checked)}
                        />
                        <span>He leído y acepto los términos de la Carta Responsiva</span>
                    </label>

                    <div className="df-carta-sig-block">
                        <label className="df-label">Firma del propietario</label>
                        {ownerSignature ? (
                            <div className="df-carta-sig-preview">
                                <img src={ownerSignature} alt="Firma" className="df-carta-sig-img" />
                                <button
                                    type="button"
                                    className="df-carta-sig-clear"
                                    onClick={() => { setOwnerSignature(''); setSignatureDirty(true); }}
                                >
                                    Volver a firmar
                                </button>
                            </div>
                        ) : (
                            <SignaturePad onChange={sig => { setOwnerSignature(sig); setLetterAccepted(true); setSignatureDirty(true); }} />
                        )}
                    </div>

                    <button
                        type="button"
                        className="df-carta-whatsapp-btn"
                        onClick={handleShareLetter}
                        disabled={!formData.primaryOwnerId}
                    >
                        <span className="df-carta-whatsapp-icon">💬</span>
                        Compartir carta por WhatsApp
                    </button>
                </div>
            </div>

            {/* ── New Owner Modal ── */}
            {showNewOwnerModal && (
                <div className="df-modal-overlay" onClick={() => setShowNewOwnerModal(false)}>
                    <div className="df-modal" onClick={e => e.stopPropagation()}>
                        <h3 className="df-modal-title">Nuevo Propietario</h3>

                        {newOwnerErrors.general && (
                            <div className="df-error-banner">{newOwnerErrors.general}</div>
                        )}

                        <div className="df-modal-field">
                            <label className="df-label">Nombre completo <span className="df-required">*</span></label>
                            <input
                                className={`df-input ${newOwnerErrors.fullName ? 'df-input--err' : ''}`}
                                type="text"
                                value={newOwnerForm.fullName}
                                onChange={e => setNewOwnerForm(p => ({ ...p, fullName: e.target.value }))}
                                placeholder="ej. María García"
                                autoComplete="off"
                            />
                            {newOwnerErrors.fullName && <span className="df-field-error">{newOwnerErrors.fullName}</span>}
                        </div>

                        <div className="df-modal-field">
                            <label className="df-label">Teléfono <span className="df-required">*</span></label>
                            <input
                                className={`df-input ${newOwnerErrors.phone ? 'df-input--err' : ''}`}
                                type="tel"
                                value={newOwnerForm.phone}
                                onChange={e => setNewOwnerForm(p => ({ ...p, phone: e.target.value }))}
                                placeholder="ej. 555-1234"
                            />
                            {newOwnerErrors.phone && <span className="df-field-error">{newOwnerErrors.phone}</span>}
                        </div>

                        <div className="df-modal-field">
                            <label className="df-label">Correo electrónico <span className="df-sub">(opcional)</span></label>
                            <input
                                className="df-input"
                                type="email"
                                value={newOwnerForm.email}
                                onChange={e => setNewOwnerForm(p => ({ ...p, email: e.target.value }))}
                                placeholder="ej. maria@ejemplo.com"
                            />
                        </div>

                        <div className="df-modal-field">
                            <label className="df-label">Dirección <span className="df-sub">(opcional)</span></label>
                            <input
                                className="df-input"
                                type="text"
                                value={newOwnerForm.address}
                                onChange={e => setNewOwnerForm(p => ({ ...p, address: e.target.value }))}
                                placeholder="ej. Calle Principal 123"
                            />
                        </div>

                        <div className="df-modal-actions">
                            <button
                                type="button"
                                className="df-cancel"
                                onClick={() => setShowNewOwnerModal(false)}
                                disabled={newOwnerSaving}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="df-submit"
                                onClick={handleCreateOwner}
                                disabled={newOwnerSaving}
                            >
                                {newOwnerSaving ? 'Guardando…' : 'Guardar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Actions ── */}
            <div className="df-actions">
                <button
                    type="submit"
                    className="df-submit"
                    disabled={loading || ownersLoading || noOwners}
                >
                    {loading
                        ? 'Guardando…'
                        : noOwners
                            ? 'Sin propietarios disponibles'
                            : editingDog ? 'Guardar cambios' : 'Agregar perro'}
                </button>
                <button type="button" className="df-cancel" onClick={handleCancel}>
                    Cancelar
                </button>
            </div>

        </form>
    );
};

export default DogForm;
